import { useCallback, useEffect, useMemo } from 'react'
import { createSelector } from 'reselect'
import moment from 'moment'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { type FlightsClassTypes } from 'constants/flight'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import { useSeatEligiblePassengers } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { isValidOccupancy } from 'lib/offer/occupancyUtils'
import { getCharterBundleCandidates, getCharterBundleRecommendation, type CharterBundleRecommendation } from 'lib/charterFlights/charterBundleRecommendation'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import useAllCharterFlightJourneys from './useAllCharterFlightJourneys'

const EMPTY_CANDIDATES: Array<CharterBundleRecommendation> = []

interface Options {
  offer?: App.HotelOffer
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  duration: number
  checkIn?: string
  checkOut?: string
  disabled?: boolean
}

/** Bundle inventory and hotel calendars are already cached by the existing Redux actions. */
export default function useCharterBundleRecommendation({ offer, rooms, fareClass, duration, checkIn, checkOut, disabled }: Options) {
  const dispatch = useAppDispatch()
  const regionEnabled = useAppSelector(isMaldivesCharterRegionEnabled)
  const policy = useCharterFareClassPolicy(offer, rooms)
  const effectiveFareClass = policy.allowedFareClasses.includes(fareClass) ? fareClass : policy.defaultFareClass
  const enabled = !disabled && regionEnabled && !!offer && scheduleIsCurrent(offer.onlinePurchaseSchedule) &&
    offer.offerFlightsEnabled && policy.isCharterOffer && !!effectiveFareClass && rooms.length > 0 && isValidOccupancy(...rooms)
  const fares = useAllCharterFlightJourneys({
    charterBundleRoute: enabled ? 'melbourne-maldives' : undefined,
    occupancies: rooms,
    fareClass: effectiveFareClass ?? fareClass,
    disabled: !enabled,
  })
  const packages = useMemo(() => offer?.packages?.filter(pkg => CHARTER_FLIGHT_DETAILS['melbourne-maldives'].validDurations.includes(pkg.duration)) ?? [], [offer])
  const calendarRequests = useMemo(() => rooms.flatMap(room => {
    const occupancyKey = generateOccupancyStringByRoom(room)
    return packages.map(pkg => ({ occupancyKey, packageKey: pkg.uniqueKey }))
  }), [rooms, packages])
  const getRelevantCalendars = useMemo(() => createSelector(
    (state: App.State) => state.calendar.calendarsByOccupancy,
    calendars => calendarRequests.map(({ occupancyKey, packageKey }) => calendars[occupancyKey]?.[packageKey]),
    reselectShallowEqual,
  ), [calendarRequests])
  const relevantCalendars = useAppSelector(getRelevantCalendars)
  const calendars = useMemo(() => {
    const result: App.CalendarState['calendarsByOccupancy'] = {}
    calendarRequests.forEach(({ occupancyKey, packageKey }, index) => {
      const calendar = relevantCalendars[index]
      if (calendar) {
        result[occupancyKey] ??= {}
        result[occupancyKey][packageKey] = calendar
      }
    })
    return result
  }, [calendarRequests, relevantCalendars])
  const getCalendarsReady = useCallback((state: App.State) => calendarRequests.every(({ occupancyKey, packageKey }) =>
    !!state.calendar.calendarsByOccupancy[occupancyKey]?.[packageKey] && !state.calendar.calendarsByOccupancyLoading[occupancyKey]?.[packageKey],
  ), [calendarRequests])
  const calendarsReady = useAppSelector(getCalendarsReady)
  useEffect(() => {
    if (enabled && offer && packages.length) dispatch(fetchCalendarsByOccupancy(offer.id, { occupants: rooms, packages }))
  }, [dispatch, enabled, offer, packages, rooms, calendarsReady])
  const seatCount = useSeatEligiblePassengers(rooms)
  const today = moment().format(ISO_DATE_FORMAT)
  const candidates = useMemo(() => {
    if (!enabled || !offer || !effectiveFareClass || !calendarsReady || fares?.fetching || fares?.error || !fares?.fares) return EMPTY_CANDIDATES
    const earliestCheckIn = moment().add(offer.bookingMinLeadDays ?? 0, 'days').format(ISO_DATE_FORMAT)
    return getCharterBundleCandidates({
      offer,
      rooms,
      fares: fares.fares,
      calendarDataByOccupancy: calendars,
      fareClass: effectiveFareClass,
      seatCount,
      today,
    }).filter(candidate => candidate.checkIn >= earliestCheckIn)
  }, [enabled, offer, effectiveFareClass, calendarsReady, fares, rooms, calendars, seatCount, today])
  const recommendation = useMemo(() => getCharterBundleRecommendation(candidates, duration, checkIn, checkOut), [candidates, duration, checkIn, checkOut])
  return { candidates, recommendation, fareClass: effectiveFareClass }
}
